import React, { useState, useMemo } from "react";
import { apiFetch } from "@/lib/api";
import { toast } from "@/components/ui/feedback";
import {
  ActionPrompt,
  ActionPromptTagGroup,
  ActionPromptTag,
} from "./actionPromptTypes";

interface ActionPromptEditorModalProps {
  prompt: ActionPrompt | null;
  groups: ActionPromptTagGroup[];
  tags: ActionPromptTag[];
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export function ActionPromptEditorModal({
  prompt,
  groups,
  tags,
  open,
  onClose,
  onSaved,
}: ActionPromptEditorModalProps) {
  const [name, setName] = useState(prompt?.name || "");
  const [text, setText] = useState(prompt?.text || "");
  const [enabled, setEnabled] = useState(prompt?.enabled ?? true);
  const [sortOrder, setSortOrder] = useState<number>(prompt?.sortOrder ?? 0);
  const [tagIds, setTagIds] = useState<string[]>(
    prompt?.tags?.map((t) => t.id) || []
  );
  const [tagConfigTouched, setTagConfigTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Enabled groups
  const enabledGroups = useMemo(
    () => groups.filter((g) => g.enabled),
    [groups]
  );

  // Match-enabled groups (gender and age_group in v1)
  const matchGroups = useMemo(
    () =>
      enabledGroups.filter(
        (g) => g.matchEnabled || g.code === "gender" || g.code === "age_group"
      ),
    [enabledGroups]
  );

  if (!open) return null;

  const tagsForGroup = (group: ActionPromptTagGroup) => {
    return tags.filter(
      (t) => t.enabled && (t.groupId === group.id || t.groupCode === group.code)
    );
  };

  const toggleTag = (group: ActionPromptTagGroup, tagId: string) => {
    let baseTagIds = tagIds;
    if (!tagConfigTouched && prompt?.id) {
      // On first tag edit, sanitize hidden/unavailable IDs
      const enabledTagIdSet = new Set(
        groups
          .filter((g) => g.enabled)
          .flatMap((g) => tags.filter((t) => t.enabled && (t.groupId === g.id || t.groupCode === g.code)).map((t) => t.id))
      );
      baseTagIds = tagIds.filter((id) => enabledTagIdSet.has(id));
    }
    setTagConfigTouched(true);

    const isSingle = group.selectionMode === "single";
    const groupTagIds = new Set(tagsForGroup(group).map((t) => t.id));

    if (isSingle) {
      setTagIds([
        ...baseTagIds.filter((id) => !groupTagIds.has(id)),
        ...(baseTagIds.includes(tagId) ? [] : [tagId]),
      ]);
    } else {
      setTagIds(
        baseTagIds.includes(tagId)
          ? baseTagIds.filter((id) => id !== tagId)
          : [...baseTagIds, tagId]
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedText = text.trim();

    if (!trimmedName) {
      toast("请填写动作名称", "error");
      return;
    }
    if (!trimmedText) {
      toast("请填写动作描述提示词", "error");
      return;
    }

    const isEditing = Boolean(prompt?.id);

    // Validation: Gender and Age group MUST have at least one tag selected if creating OR if tag config was touched!
    if (!isEditing || tagConfigTouched) {
      for (const group of matchGroups) {
        const groupTagList = tagsForGroup(group);
        const hasSelectedTag = groupTagList.some((t) => tagIds.includes(t.id));
        if (!hasSelectedTag) {
          toast(`必须为必选匹配维度「${group.name}」至少选择一个标签`, "error");
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      if (isEditing && prompt?.id) {
        // Edit existing prompt: if tag-config was NOT touched, OMIT tagIds and constraints completely!
        // This preserves disabled stored references without failing backend validation.
        const payload: Record<string, unknown> = {
          name: trimmedName,
          text: trimmedText,
          enabled,
          sortOrder: Number(sortOrder) || 0,
        };

        if (tagConfigTouched) {
          const constraints: Record<string, string> = {};
          for (const group of matchGroups) {
            constraints[group.code] = "include";
          }
          const enabledTagIdSet = new Set(
            groups
              .filter((g) => g.enabled)
              .flatMap((g) => tags.filter((t) => t.enabled && (t.groupId === g.id || t.groupCode === g.code)).map((t) => t.id))
          );
          payload.tagIds = tagIds.filter((id) => enabledTagIdSet.has(id));
          payload.constraints = constraints;
        }

        const res = await apiFetch(`/faceswap/action-prompts/${prompt.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (res.status === 409) {
          toast("保存失败：动作名称已被使用 (409)", "error");
          return;
        }
        if (!res.ok) {
          throw new Error(await res.text());
        }
        toast("动作提示词已更新", "success");
      } else {
        // Create new prompt: always sends complete config!
        const constraints: Record<string, string> = {};
        for (const group of matchGroups) {
          constraints[group.code] = "include";
        }
        const payload = {
          name: trimmedName,
          text: trimmedText,
          enabled,
          sortOrder: Number(sortOrder) || 0,
          tagIds,
          constraints,
        };
        const res = await apiFetch("/faceswap/action-prompts", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (res.status === 409) {
          toast("创建失败：动作名称已被使用 (409)", "error");
          return;
        }
        if (!res.ok) {
          throw new Error(await res.text());
        }
        toast("动作提示词已创建", "success");
      }

      onSaved();
      onClose();
    } catch (err) {
      toast(
        "保存失败：" + (err instanceof Error ? err.message : String(err)),
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={() => !submitting && onClose()} />
      <div className="relative bg-white rounded-xl w-full max-w-xl mx-4 overflow-hidden animate-[popIn_0.25s_ease-out] max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 shrink-0">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {prompt ? "编辑动作提示词" : "新建动作提示词"}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              配置写实自拍人物姿态描述。性别与年龄段为运行时匹配维度，必须各至少选择一项。
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        {/* Scrollable Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto flex-1">
          {/* Action Name & Sort Order */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                动作名称 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="例如：西陵江畔扶栏远眺"
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                排序权重
              </label>
              <input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
            </div>
          </div>

          {/* Action Prompt Text */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">
                动作描述文本 (Prompt) <span className="text-red-500">*</span>
              </label>
              <span className="text-xs text-gray-400 font-mono">
                {text.trim().length} 字
              </span>
            </div>
            <textarea
              required
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="例如：单手轻扶在身旁栏杆上，侧身微笑着望向江面远方，眼神清澈，神态自然轻松，身体姿态与环境相协调。"
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 resize-none leading-relaxed font-mono"
            />
            <p className="mt-1 text-xs text-gray-400">
              描述人物在场景中的身体姿态、双手交互与神态动作。运行时将作为四段提示词的第 3 段（模板提示词与人物年龄性别提示词之后、末尾固定提示词之前）按命中规则自动追加。
            </p>
          </div>

          {/* Enabled Checkbox */}
          <div className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              id="prompt-enabled-toggle"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500 accent-teal-600 cursor-pointer"
            />
            <label
              htmlFor="prompt-enabled-toggle"
              className="text-sm text-gray-700 cursor-pointer select-none"
            >
              启用此动作提示词（禁用后不会被新生成的模板任务命中）
            </label>
          </div>

          {/* Tag Configuration Section */}
          <div className="pt-3 border-t border-gray-100 space-y-3">
            <div>
              <h4 className="text-sm font-semibold text-gray-900">适用人像特征标签</h4>
              <p className="text-xs text-gray-500 mt-0.5">
                性别与年龄段为必须配置的匹配维度，支持多选（例如若男女均适用可同时勾选男性和女性）。
              </p>
            </div>

            <div className="space-y-3">
              {enabledGroups.map((group) => {
                const isMatch = group.matchEnabled || group.code === "gender" || group.code === "age_group";
                const isSingle = group.selectionMode === "single";
                const groupTags = tagsForGroup(group);
                const selectedCount = groupTags.filter((t) => tagIds.includes(t.id)).length;
                const isMissingRequired = isMatch && selectedCount === 0;

                return (
                  <div
                    key={group.id}
                    className={`p-3 rounded-lg border transition-colors ${
                      isMissingRequired
                        ? "bg-rose-50/40 border-rose-200"
                        : "bg-gray-50/70 border-gray-100"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-900">
                          {group.name}
                        </span>
                        {isMatch ? (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-rose-50 text-rose-700 border border-rose-200">
                            <span className="text-rose-600 font-bold">*</span>必选匹配 (多选)
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-200">
                            元数据 ({isSingle ? "单选" : "多选"})
                          </span>
                        )}
                      </div>
                      {isMissingRequired && (
                        <span className="text-[11px] text-rose-600 font-medium">
                          请至少选择一项
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {groupTags.map((tag) => {
                        const isSelected = tagIds.includes(tag.id);
                        return (
                          <button
                            type="button"
                            key={tag.id}
                            onClick={() => toggleTag(group, tag.id)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                              isSelected
                                ? isSingle
                                  ? "bg-teal-600 text-white shadow-sm ring-2 ring-teal-600/30"
                                  : "bg-teal-600 text-white shadow-sm"
                                : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-100"
                            }`}
                          >
                            <i
                              className={`text-[11px] ${
                                isSelected
                                  ? isSingle
                                    ? "ri-radio-button-fill text-teal-200"
                                    : "ri-checkbox-fill text-teal-200"
                                  : isSingle
                                  ? "ri-checkbox-blank-circle-line text-gray-400"
                                  : "ri-checkbox-blank-line text-gray-400"
                              }`}
                            />
                            <span>{tag.name}</span>
                          </button>
                        );
                      })}

                      {groupTags.length === 0 && (
                        <span className="text-xs text-gray-400 italic">
                          该标签组下暂无启用标签，请先在标签管理中添加
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}

              {enabledGroups.length === 0 && (
                <div className="text-xs text-gray-400 p-3 bg-gray-50 rounded-lg text-center">
                  暂无启用的标签组，请先在标签管理中配置
                </div>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
            <button
              type="button"
              disabled={submitting}
              onClick={onClose}
              className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap disabled:opacity-60"
            >
              {submitting ? "保存中..." : prompt ? "更新动作提示词" : "创建动作提示词"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
